import React, { useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, CheckCircle, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';

export default function ThankYouPage() {
  const conversionFired = useRef(false);
  
  useEffect(() => {
    // Only fire conversion if user just submitted a quote (not on direct visit/refresh)
    // This prevents inflating Google Ads conversions
    const wasSubmitted = sessionStorage.getItem('quote_submitted');
    
    if (window.gtag && wasSubmitted && !conversionFired.current) {
      conversionFired.current = true;
      // Clear the flag immediately to prevent re-firing on refresh
      sessionStorage.removeItem('quote_submitted');
      
      // Fire Google Ads conversion event
      // Format: 'AW-CONVERSION_ID/CONVERSION_LABEL'
      window.gtag('event', 'conversion', {
        'send_to': 'AW-17855604804/nwAYCLPT2t8bEMTQm8JC',
        'value': 1.0,
        'currency': 'AUD'
      });
      console.log('Google Ads conversion fired: AW-17855604804/nwAYCLPT2t8bEMTQm8JC');
    }
  }, []);

  return (
    <>
      <Helmet>
        <title>Thank You - Quote Request Received | Thrifty Move</title>
        <meta name="description" content="Thank you for your quote request. Our team will contact you shortly." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Header />

      <div className="min-h-screen bg-gradient-to-br from-green-50 to-gray-50 flex items-center justify-center py-32">
        <div className="container mx-auto px-4">
          <div className="max-w-2xl mx-auto text-center">
            {/* Success Icon */}
            <div className="mb-8">
              <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
                <CheckCircle className="w-16 h-16 text-white" />
              </div>
            </div>

            {/* Headline */}
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Thanks! We've Received Your Request
            </h1>

            {/* Text */}
            <p className="text-xl text-gray-700 mb-8">
              Our team will contact you shortly with your personalized moving quote.
              <br />
              <span className="font-semibold">If your move is urgent, call us now.</span>
            </p>

            {/* Phone Number - Big */}
            <div className="bg-white rounded-2xl shadow-xl p-8 mb-8">
              <p className="text-gray-600 mb-3 text-lg">Call us directly:</p>
              <a 
                href="tel:1800233023"
                className="text-4xl md:text-5xl font-bold text-yellow-600 hover:text-yellow-700 transition-colors"
                onClick={() => {
                  // Track phone click as GA4 event (not conversion)
                  // Phone call conversions handled by gtag phone_conversion_number
                  if (window.gtag) {
                    window.gtag('event', 'phone_click', {
                      'event_category': 'Phone',
                      'event_label': 'Call Now - Thank You Page'
                    });
                  }
                }}
                data-phone-number="true"
              >
                📞 <span data-phone-number="true">1800 233 023</span>
              </a>
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a 
                href="tel:1800233023"
                onClick={() => {
                  // Track phone click as GA4 event (not conversion)
                  if (window.gtag) {
                    window.gtag('event', 'phone_click', {
                      'event_category': 'Phone',
                      'event_label': 'Call Now Button - Thank You Page'
                    });
                  }
                }}
                data-phone-number="true"
              >
                <Button className="bg-yellow-500 hover:bg-yellow-600 text-gray-900 font-bold text-xl px-10 py-6">
                  <Phone className="mr-2 w-6 h-6" />
                  Call Now
                </Button>
              </a>
              <a href="https://wa.me/61487987277" target="_blank" rel="noopener noreferrer">
                <Button className="bg-green-600 hover:bg-green-700 text-white font-bold text-xl px-10 py-6">
                  <MessageCircle className="mr-2 w-6 h-6" />
                  WhatsApp Us
                </Button>
              </a>
            </div>

            {/* What Happens Next */}
            <div className="bg-white rounded-xl p-8 text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-4 text-center">What Happens Next?</h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">We Review Your Request</h3>
                    <p className="text-gray-600">Our team reviews your moving details</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">We'll Call You Soon</h3>
                    <p className="text-gray-600">We'll call you as soon as possible during business hours.</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Get Your Quote</h3>
                    <p className="text-gray-600">Receive your personalized moving quote</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 bg-yellow-500 rounded-full flex items-center justify-center flex-shrink-0 mt-1">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 mb-1">Book Your Move</h3>
                    <p className="text-gray-600">Confirm your moving date and we'll take care of the rest</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Back to Home */}
            <div className="mt-8">
              <a href="/" className="text-gray-600 hover:text-gray-900 underline">
                ← Back to Homepage
              </a>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </>
  );
}
